import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronRight, ChevronLeft, Bell, Users, Loader2, X, Globe, Check } from 'lucide-react';
import { useAppState } from '../../context/AppStateContext';
import { useAuth } from '../../context/AuthContext';
import { useResponsive } from '../../hooks/useResponsive';
import { GROUPS_LIST, NICHE_IMAGES } from '../../utils/constants';
import DealInfoDrawer from '../shop/DealInfoDrawer';
import GiftCardFlow from '../shop/GiftCardFlow';
import PayBillSlider from '../shop/PayBillSlider';
import { providersApi, servicesApi } from '../../services/api';
import { COUNTRY_NAMES } from '../../utils/currency';

// Country name aliases for flexible matching
// Maps various country name formats to a normalized key
const COUNTRY_ALIASES = {
  'usa': ['united states', 'united states of america', 'us', 'u.s.', 'u.s.a.', 'america'],
  'united states': ['usa', 'united states of america', 'us', 'u.s.', 'u.s.a.', 'america'],
  'united states of america': ['usa', 'united states', 'us', 'u.s.', 'u.s.a.', 'america'],
  'uk': ['united kingdom', 'great britain', 'gb', 'britain', 'england'],
  'united kingdom': ['uk', 'great britain', 'gb', 'britain', 'england'],
  'uae': ['united arab emirates', 'emirates'],
  'united arab emirates': ['uae', 'emirates'],
};

// Helper to check if two country names match (handles aliases)
const countriesMatch = (country1, country2) => {
  if (!country1 || !country2) return false;

  const c1 = country1.toLowerCase().trim();
  const c2 = country2.toLowerCase().trim();

  // Direct match
  if (c1 === c2) return true;

  // Check aliases
  const c1Aliases = COUNTRY_ALIASES[c1] || [];
  const c2Aliases = COUNTRY_ALIASES[c2] || [];

  // c1 matches one of c2's aliases
  if (c2Aliases.includes(c1)) return true;
  // c2 matches one of c1's aliases
  if (c1Aliases.includes(c2)) return true;

  return false;
};

// Helper to check if provider matches country (checks both country name and country_code)
const providerMatchesCountry = (provider, targetCountry) => {
  if (!provider || !targetCountry) return false;

  const target = targetCountry.toLowerCase().trim();

  // Check direct match with country_code (e.g., "US" === "us")
  if (provider.country_code?.toLowerCase() === target) return true;

  // Check match with country name using flexible matching
  if (countriesMatch(provider.country, targetCountry)) return true;

  return false;
};

// Helper to extract provider display name (removes country prefix from name)
// e.g., "Brazil Nextel" -> "Nextel", "Nigeria MTN" -> "MTN"
const getProviderDisplayName = (provider) => {
  if (!provider?.name) return 'Provider';
  const name = provider.name;
  const country = provider.country;

  // If country is available and name starts with country, remove it
  if (country && name.toLowerCase().startsWith(country.toLowerCase())) {
    const cleanName = name.substring(country.length).trim();
    return cleanName || name; // Return original if nothing left after removal
  }

  // Fallback: split by first space and return the rest
  const parts = name.split(' ');
  if (parts.length > 1) {
    return parts.slice(1).join(' ');
  }

  return name;
};

export default function ShopTab() {
  const navigate = useNavigate();
  const {
    shopSubTab,
    setShopSubTab,
    isolatedGroupDeals,
    joinedGroupIds,
    setSelectedDeal,
    setShowDealInfoDrawer,
    setSelectedGroup,
    setShowNotifications,
    unreadCount,
    selectedProvider,
    setSelectedProvider,
    showPayBillSlider,
    setShowPayBillSlider,
    profileForm,
  } = useAppState();
  const { isDesktop } = useResponsive();
  const { user } = useAuth();

  // Get time-based greeting (matches HomeTab)
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };
  const userFirstName = user?.first_name || profileForm.firstName || 'there';

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGiftCard, setSelectedGiftCard] = useState(null);

  // Country filter state
  const [showCountrySlider, setShowCountrySlider] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null); // null means use profile country

  // Provider API state
  const [providers, setProviders] = useState([]);
  const [services, setServices] = useState([]);
  const [giftCardProvidersList, setGiftCardProvidersList] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [providersError, setProvidersError] = useState(null);

  // Fetch providers and services on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoadingProviders(true);
      setProvidersError(null);
      try {
        // Fetch services first
        const servicesData = await servicesApi.getAll();
        setServices(servicesData || []);

        if (!servicesData || servicesData.length === 0) {
          // Fallback: fetch all providers if no services available
          const allProviders = await providersApi.getAllProviders();
          setProviders(allProviders || []);
          return;
        }

        // Identify featured store services (Mobile Data, Mobile Top Up, Airtime) - exclude Utilities
        const featuredServices = servicesData.filter(s => {
          const name = s.name?.toLowerCase() || '';
          // Exclude utilities
          if (name.includes('utilit')) return false;
          return name.includes('data') ||
                 name.includes('top up') ||
                 name.includes('topup') ||
                 name.includes('airtime');
        });

        // Identify gift card services (Pin, Voucher, Gift)
        const giftCardServices = servicesData.filter(s => {
          const name = s.name?.toLowerCase() || '';
          return name.includes('pin') ||
                 name.includes('voucher') ||
                 name.includes('gift');
        });

        // Fetch providers for featured services
        const featuredServiceIds = featuredServices.map(s => s.service_uuid);
        const featuredProviderPromises = featuredServiceIds.map(serviceId =>
          providersApi.getByService(serviceId).catch(() => [])
        );

        // Fetch providers for gift card services
        const giftCardServiceIds = giftCardServices.map(s => s.service_uuid);
        const giftCardProviderPromises = giftCardServiceIds.map(serviceId =>
          providersApi.getByService(serviceId).catch(() => [])
        );

        // Fetch both in parallel
        const [featuredProviderArrays, giftCardProviderArrays] = await Promise.all([
          Promise.all(featuredProviderPromises),
          Promise.all(giftCardProviderPromises),
        ]);

        // Combine featured providers and remove duplicates
        const allFeaturedProviders = featuredProviderArrays.flat();
        const uniqueFeaturedProviders = Array.from(
          new Map(allFeaturedProviders.map(p => [p.provider_uuid, p])).values()
        );

        // Combine gift card providers and remove duplicates
        const allGiftCardProviders = giftCardProviderArrays.flat();
        const uniqueGiftCardProviders = Array.from(
          new Map(allGiftCardProviders.map(p => [p.provider_uuid, p])).values()
        );

        console.log('Fetched gift card providers:', uniqueGiftCardProviders.length);

        setProviders(uniqueFeaturedProviders);
        setGiftCardProvidersList(uniqueGiftCardProviders);
      } catch (err) {
        console.error('Failed to fetch data:', err);
        setProvidersError('Failed to load stores');
        setProviders([]);
        setServices([]);
        setGiftCardProvidersList([]);
      } finally {
        setLoadingProviders(false);
      }
    };
    fetchData();
  }, []);

  // Get service UUIDs for gift card filtering
  const giftCardServiceUuids = useMemo(() => {
    return services
      .filter(s =>
        s.name?.toLowerCase().includes('pin') ||
        s.name?.toLowerCase().includes('voucher') ||
        s.name?.toLowerCase().includes('gift')
      )
      .map(s => s.service_uuid);
  }, [services]);

  // Featured Stores = providers NOT in gift card services
  const featuredStoreProviders = useMemo(() => {
    if (giftCardServiceUuids.length === 0) return providers;
    return providers.filter(p => !giftCardServiceUuids.includes(p.service_uuid));
  }, [providers, giftCardServiceUuids]);

  // Gift Cards = providers from gift card services (pin, voucher, gift)
  const giftCardProviders = useMemo(() => {
    return giftCardProvidersList;
  }, [giftCardProvidersList]);


  // Extract unique countries from all providers (featured + gift cards)
  const availableCountries = useMemo(() => {
    const countries = new Set();
    providers.forEach(p => {
      if (p.country) countries.add(p.country);
    });
    giftCardProviders.forEach(p => {
      if (p.country) countries.add(p.country);
    });
    return Array.from(countries).sort();
  }, [providers, giftCardProviders]);

  // Get active country filter (selected country or user's profile country)
  // User data from AuthContext is the authoritative source
  // Profile stores country CODE (e.g., "NG"), but API returns full NAME (e.g., "Nigeria")
  // Convert profile country code to full name for comparison
  const userCountryRaw = user?.country || user?.country_code || profileForm?.country;
  const profileCountryName = userCountryRaw ? COUNTRY_NAMES[userCountryRaw] || userCountryRaw : null;
  const activeCountry = selectedCountry || profileCountryName || null;


  // Filter featured store providers by country (checks both country name and country_code)
  const countryFilteredFeaturedProviders = useMemo(() => {
    if (!activeCountry) return featuredStoreProviders;
    return featuredStoreProviders.filter(p => providerMatchesCountry(p, activeCountry));
  }, [featuredStoreProviders, activeCountry]);

  // Filter gift card providers by country (checks both country name and country_code)
  const countryFilteredGiftCardProviders = useMemo(() => {
    if (!activeCountry) return giftCardProviders;
    return giftCardProviders.filter(p => providerMatchesCountry(p, activeCountry));
  }, [giftCardProviders, activeCountry]);

  // Flatten all deals
  const allDeals = Object.values(isolatedGroupDeals).flat();

  // Filter deals by search query
  const filteredDeals = searchQuery
    ? allDeals.filter((d) => d.brand.toLowerCase().includes(searchQuery.toLowerCase()))
    : allDeals;

  // Separate deals by joined/not joined groups
  const forYouDeals = filteredDeals.filter((deal) => joinedGroupIds.includes(deal.groupId));
  const moreDeals = filteredDeals.filter((deal) => !joinedGroupIds.includes(deal.groupId));

  // Get groups the user hasn't joined yet
  const unjoinedGroups = GROUPS_LIST.filter((group) => !joinedGroupIds.includes(group.id));

  // Filter groups by search query (search by group name or deals within)
  const filteredGroups = searchQuery
    ? unjoinedGroups.filter((group) => {
        const groupDeals = isolatedGroupDeals[group.id] || [];
        const matchesGroupName = group.name.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesDeal = groupDeals.some((d) => d.brand.toLowerCase().includes(searchQuery.toLowerCase()));
        return matchesGroupName || matchesDeal;
      })
    : unjoinedGroups;

  // Handle "For You" deal click - route directly to group chat
  const handleForYouDealClick = (deal) => {
    const group = GROUPS_LIST.find((g) => g.id === deal.groupId);
    if (group) {
      setSelectedGroup(group);
      navigate('/groups');
    }
  };

  // Handle "More Deals" join button - open drawer with first deal from group
  const handleJoinGroupClick = (deal) => {
    setSelectedDeal(deal);
    setShowDealInfoDrawer(true);
  };

  // Handle group card join button - open drawer with first deal from that group
  const handleGroupJoinClick = (group) => {
    const groupDeals = isolatedGroupDeals[group.id] || [];
    if (groupDeals.length > 0) {
      setSelectedDeal(groupDeals[0]);
      setShowDealInfoDrawer(true);
    }
  };

  // Handle provider click - open Pay Bill slider
  const handleProviderClick = (provider) => {
    setSelectedProvider(provider);
    setShowPayBillSlider(true);
  };

  // Filter featured store providers by search query (using country-filtered list)
  const filteredProviders = searchQuery
    ? countryFilteredFeaturedProviders.filter((p) => p.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : countryFilteredFeaturedProviders;

  // Filter gift card providers by search query (using country-filtered list)
  const filteredGiftCardProviders = searchQuery
    ? countryFilteredGiftCardProviders.filter((p) => p.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : countryFilteredGiftCardProviders;

  // Handle country selection from slider
  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setShowCountrySlider(false);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto scrollbar-hide">
        {/* Mobile Header - matches HomeTab */}
        {!isDesktop && (
          <header className="sticky top-0 bg-white z-10 px-4 pb-3 flex items-center justify-between border-b border-gray-100 pt-[calc(0.75rem+env(safe-area-inset-top))]">
            <div className="flex items-center gap-3">
              <img src="/logo/bonndleLogoIcon_Indigo.svg" alt="Bonndle" className="h-9 w-9" />
              <h1 className="font-bold text-gray-900 text-lg">
                {getGreeting()}, {userFirstName}
              </h1>
            </div>
            <button
              onClick={() => setShowNotifications(true)}
              className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-full"
            >
              <Bell size={22} />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-green-500 rounded-full" />
              )}
            </button>
          </header>
        )}
        <div className={`px-4 py-6 ${isDesktop ? 'max-w-4xl mx-auto' : ''}`}>
        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search brands..."
            className="w-full pl-12 pr-4 py-3 bg-gray-100 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* Toggle Switch */}
        <div className="flex bg-gray-100 rounded-xl p-1 mb-6">
          <button
            onClick={() => setShopSubTab('deals')}
            className={`flex-1 py-2.5 rounded-lg font-medium text-sm transition-colors ${
              shopSubTab === 'deals'
                ? 'bg-white text-indigo-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Deals
          </button>
          <button
            onClick={() => setShopSubTab('giftcards')}
            className={`flex-1 py-2.5 rounded-lg font-medium text-sm transition-colors ${
              shopSubTab === 'giftcards'
                ? 'bg-white text-indigo-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Gift Cards
          </button>
        </div>

        {/* Content */}
        {shopSubTab === 'deals' ? (
          <div className="space-y-8">
            {/* FOR YOU Section - Horizontal Carousel */}
            {forYouDeals.length > 0 && (
              <ForYouCarousel
                deals={forYouDeals}
                onDealClick={handleForYouDealClick}
              />
            )}

            {/* FEATURED STORES Section - Providers with 3% Cashback */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <h3 className="font-black text-lg text-gray-900 tracking-tight">Featured Stores</h3>
                  {activeCountry && (
                    <span className="bg-indigo-100 text-indigo-700 text-xs font-bold px-2 py-0.5 rounded-full">
                      {activeCountry}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setShowCountrySlider(true)}
                  className="text-indigo-600 text-sm font-bold hover:text-indigo-700 flex items-center gap-1"
                >
                  <Globe size={14} />
                  View All
                </button>
              </div>
              {loadingProviders ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 size={24} className="animate-spin text-indigo-600" />
                  <span className="ml-2 text-gray-500">Loading stores...</span>
                </div>
              ) : providersError ? (
                <div className="text-center py-12">
                  <p className="text-red-500 text-sm">{providersError}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="mt-2 text-indigo-600 text-sm font-bold"
                  >
                    Retry
                  </button>
                </div>
              ) : filteredProviders.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-400">No stores found</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {filteredProviders.map((provider) => (
                    <ProviderCard
                      key={provider.provider_uuid || provider.id}
                      provider={provider}
                      onClick={() => handleProviderClick(provider)}
                    />
                  ))}
                </div>
              )}
            </section>


            {/* Empty State */}
            {forYouDeals.length === 0 && moreDeals.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-400">No deals found</p>
              </div>
            )}
          </div>
        ) : (
          <GiftCardsContent
            providers={filteredGiftCardProviders}
            loading={loadingProviders}
            error={providersError}
            onSelectProvider={handleProviderClick}
          />
          )}
        </div>
      </div>

      {/* Deal Info Drawer */}
      <DealInfoDrawer />

      {/* Gift Card Purchase Flow */}
      {selectedGiftCard && (
        <GiftCardFlow
          selectedCard={selectedGiftCard}
          onClose={() => setSelectedGiftCard(null)}
        />
      )}

      {/* Pay Bill Slider */}
      {showPayBillSlider && selectedProvider && (
        <PayBillSlider
          provider={selectedProvider}
          onClose={() => {
            setShowPayBillSlider(false);
            setSelectedProvider(null);
          }}
        />
      )}

      {/* Country Selection Slider */}
      {showCountrySlider && (
        <CountrySlider
          countries={availableCountries}
          selectedCountry={activeCountry}
          providers={featuredStoreProviders}
          onSelectCountry={handleCountrySelect}
          onClose={() => setShowCountrySlider(false)}
          onProviderClick={handleProviderClick}
        />
      )}
    </div>
  );
}

// For You Carousel with scroll navigation
function ForYouCarousel({ deals, onDealClick }) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 10);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', checkScroll);
      return () => el.removeEventListener('scroll', checkScroll);
    }
  }, [deals]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const cardWidth = 200; // Card width + gap
      const scrollAmount = direction === 'right' ? cardWidth : -cardWidth;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-black text-lg text-gray-900 tracking-tight">For You</h3>
        <div className="flex items-center gap-2">
          {canScrollLeft && (
            <button
              onClick={() => scroll('left')}
              className="p-2 bg-gray-100 rounded-full text-gray-600 hover:bg-gray-200 active:scale-95 transition-all"
            >
              <ChevronLeft size={18} />
            </button>
          )}
          {canScrollRight && (
            <button
              onClick={() => scroll('right')}
              className="p-2 bg-indigo-100 rounded-full text-indigo-600 hover:bg-indigo-200 active:scale-95 transition-all"
            >
              <ChevronRight size={18} />
            </button>
          )}
        </div>
      </div>
      <div
        ref={scrollRef}
        className="overflow-x-auto -mx-4 px-4 pb-2 scrollbar-hide"
      >
        <div className="flex gap-4" style={{ width: 'max-content' }}>
          {deals.map((deal) => (
            <ForYouDealCard
              key={deal.id}
              deal={deal}
              onClick={() => onDealClick(deal)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

// For You Deal Card - Horizontal scrolling, routes to group chat
function ForYouDealCard({ deal, onClick }) {
  const group = GROUPS_LIST.find((g) => g.id === deal.groupId);

  return (
    <button
      onClick={onClick}
      className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 transition-all active:scale-[0.98] cursor-pointer text-left hover:shadow-md"
      style={{ width: '180px' }}
    >
      {/* Image */}
      <div className="relative h-32">
        <img src={deal.image} alt={deal.brand} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

        {/* Offer Badge */}
        <div className="absolute top-2 left-2 bg-white/95 px-2 py-1 rounded-full shadow-lg">
          <span className="text-[10px] font-black text-indigo-600 uppercase tracking-tight">
            {deal.offer}
          </span>
        </div>

        {/* Brand Name */}
        <div className="absolute bottom-2 left-2 right-2 text-white">
          <h4 className="font-black text-sm leading-tight uppercase tracking-tight truncate">{deal.brand}</h4>
        </div>
      </div>

      {/* Footer with Group Info */}
      <div className="p-3">
        <div className="flex items-center gap-1">
          <Users size={12} className="text-indigo-500" />
          <span className="text-[10px] font-bold text-gray-600 truncate">#{group?.name}</span>
        </div>
      </div>
    </button>
  );
}

// Store Card - 2-column grid, similar to Gift Cards style
function StoreCard({ deal, onClick }) {
  return (
    <button
      onClick={onClick}
      className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center gap-3 cursor-pointer active:scale-[0.98] transition-all hover:border-indigo-200 text-center"
    >
      <div className={`w-14 h-14 ${deal.color || 'bg-indigo-600'} rounded-2xl flex items-center justify-center text-white font-black text-xl shadow-lg`}>
        {deal.logo}
      </div>
      <div>
        <h4 className="font-black text-gray-900 text-sm">{deal.brand}</h4>
        <p className="text-emerald-600 font-bold text-[10px] uppercase tracking-tight mt-0.5">{deal.offer}</p>
      </div>
    </button>
  );
}

// Gift Cards Content - 2-column grid on mobile, 4-column grid on desktop
function GiftCardsContent({ providers, loading, error, onSelectProvider }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={24} className="animate-spin text-indigo-600" />
        <span className="ml-2 text-gray-500">Loading gift cards...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 text-sm">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-2 text-indigo-600 text-sm font-bold"
        >
          Retry
        </button>
      </div>
    );
  }

  if (providers.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">No gift cards found</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {providers.map((provider) => (
        <GiftCardProviderCard
          key={provider.provider_uuid || provider.id}
          provider={provider}
          onClick={() => onSelectProvider(provider)}
        />
      ))}
    </div>
  );
}

// Gift Card Provider Card
function GiftCardProviderCard({ provider, onClick }) {
  const [imageError, setImageError] = useState(false);
  const displayName = getProviderDisplayName(provider);

  return (
    <button
      onClick={onClick}
      className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center gap-3 cursor-pointer active:scale-[0.98] transition-all hover:border-indigo-200 text-center"
    >
      {provider.logo_url && !imageError ? (
        <div className="w-14 h-14 rounded-2xl bg-white border border-gray-100 shadow-lg flex items-center justify-center p-1.5">
          <img
            src={provider.logo_url}
            alt={displayName}
            className="w-full h-full object-contain"
            onError={() => setImageError(true)}
          />
        </div>
      ) : (
        <div className="w-14 h-14 bg-purple-600 rounded-2xl flex items-center justify-center text-white font-black text-xl shadow-lg">
          {displayName.charAt(0) || 'G'}
        </div>
      )}
      <div>
        <h4 className="font-black text-gray-900 text-sm truncate max-w-[120px]">{displayName}</h4>
        <p className="text-emerald-600 font-bold text-[10px] uppercase tracking-tight mt-0.5">3% Cashback</p>
      </div>
    </button>
  );
}

// Provider Card - 2-column grid for bill payment providers
function ProviderCard({ provider, onClick }) {
  const [imageError, setImageError] = useState(false);
  const displayName = getProviderDisplayName(provider);

  return (
    <button
      onClick={onClick}
      className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center gap-3 cursor-pointer active:scale-[0.98] transition-all hover:border-indigo-200 text-center"
    >
      {provider.logo_url && !imageError ? (
        <div className="w-14 h-14 rounded-2xl bg-white border border-gray-100 shadow-lg flex items-center justify-center p-1.5">
          <img
            src={provider.logo_url}
            alt={displayName}
            className="w-full h-full object-contain"
            onError={() => setImageError(true)}
          />
        </div>
      ) : (
        <div className="w-14 h-14 bg-indigo-600 rounded-2xl flex items-center justify-center text-white font-black text-xl shadow-lg">
          {displayName.charAt(0) || 'P'}
        </div>
      )}
      <div>
        <h4 className="font-black text-gray-900 text-sm truncate max-w-[120px]">{displayName}</h4>
        <p className="text-emerald-600 font-bold text-[10px] uppercase tracking-tight mt-0.5">3% Cashback</p>
      </div>
    </button>
  );
}

// Country Selection Slider (side panel like PayBillSlider)
function CountrySlider({ countries, selectedCountry, providers, onSelectCountry, onClose, onProviderClick }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('countries'); // 'countries' | 'providers'
  const [tempSelectedCountry, setTempSelectedCountry] = useState(selectedCountry || countries[0]);

  // Filter countries by search
  const filteredCountries = searchQuery
    ? countries.filter(c => c.toLowerCase().includes(searchQuery.toLowerCase()))
    : countries;

  // Get providers for selected country (using flexible matching with country_code)
  const countryProviders = useMemo(() => {
    if (!tempSelectedCountry) return [];
    return providers.filter(p => providerMatchesCountry(p, tempSelectedCountry));
  }, [providers, tempSelectedCountry]);

  // Filter providers by search in provider view
  const filteredProviders = searchQuery && viewMode === 'providers'
    ? countryProviders.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : countryProviders;

  // Handle country selection - show providers
  const handleCountryClick = (country) => {
    setTempSelectedCountry(country);
    setViewMode('providers');
    setSearchQuery('');
  };

  // Handle back to countries
  const handleBackToCountries = () => {
    setViewMode('countries');
    setSearchQuery('');
  };

  // Handle apply country filter
  const handleApplyFilter = () => {
    onSelectCountry(tempSelectedCountry);
  };

  return (
    <>
      {/* Backdrop */}
      <div className="backdrop animate-fade-in" onClick={onClose} />

      {/* Slide Panel (from right side) */}
      <div className="slide-panel">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              {viewMode === 'providers' && (
                <button
                  onClick={handleBackToCountries}
                  className="p-2 -ml-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
                >
                  <ChevronLeft size={20} />
                </button>
              )}
              <h2 className="text-lg font-bold text-gray-900">
                {viewMode === 'countries' ? 'Select Country' : tempSelectedCountry}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
            >
              <X size={20} />
            </button>
          </div>

          {/* Search */}
          <div className="p-4 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={viewMode === 'countries' ? 'Search countries...' : 'Search stores...'}
                className="w-full pl-11 pr-4 py-3 bg-gray-100 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {viewMode === 'countries' ? (
              <>
                {/* Countries list */}
                <div className="space-y-2">
                  {filteredCountries.length === 0 ? (
                    <p className="text-center text-gray-400 py-8">No countries found</p>
                  ) : (
                    filteredCountries.map((country) => {
                      const countryCount = providers.filter(p =>
                        providerMatchesCountry(p, country)
                      ).length;
                      const isSelected = countriesMatch(tempSelectedCountry, country);

                      return (
                        <button
                          key={country}
                          onClick={() => handleCountryClick(country)}
                          className={`w-full p-4 rounded-xl border-2 text-left flex items-center justify-between transition-all ${
                            isSelected
                              ? 'border-indigo-500 bg-indigo-50'
                              : 'border-gray-100 bg-white hover:border-indigo-200'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{getCountryFlag(country)}</span>
                            <span className={`font-bold ${isSelected ? 'text-indigo-700' : 'text-gray-900'}`}>
                              {country}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400">{countryCount} stores</span>
                            {isSelected && <Check size={18} className="text-indigo-600" />}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </>
            ) : (
              /* Providers Grid */
              <div className="grid grid-cols-2 gap-3">
                {filteredProviders.length === 0 ? (
                  <p className="col-span-2 text-center text-gray-400 py-8">No stores found</p>
                ) : (
                  filteredProviders.map((provider) => (
                    <ProviderCardSmall
                      key={provider.provider_uuid || provider.id}
                      provider={provider}
                      onClick={() => {
                        onProviderClick(provider);
                        onClose();
                      }}
                    />
                  ))
                )}
              </div>
            )}
          </div>

        </div>
      </div>
    </>
  );
}

// Small Provider Card for slider grid
function ProviderCardSmall({ provider, onClick }) {
  const [imageError, setImageError] = useState(false);
  const displayName = getProviderDisplayName(provider);

  return (
    <button
      onClick={onClick}
      className="bg-white p-3 rounded-xl shadow-sm border border-gray-100 flex flex-col items-center gap-2 cursor-pointer active:scale-[0.98] transition-all hover:border-indigo-200 text-center"
    >
      {provider.logo_url && !imageError ? (
        <div className="w-12 h-12 rounded-xl bg-white border border-gray-100 shadow flex items-center justify-center p-1">
          <img
            src={provider.logo_url}
            alt={displayName}
            className="w-full h-full object-contain"
            onError={() => setImageError(true)}
          />
        </div>
      ) : (
        <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow">
          {displayName.charAt(0) || 'P'}
        </div>
      )}
      <div>
        <h4 className="font-bold text-gray-900 text-xs truncate max-w-[100px]">{displayName}</h4>
        <p className="text-emerald-600 font-bold text-[9px] uppercase tracking-tight">3% Cashback</p>
      </div>
    </button>
  );
}

// Helper to get country flag emoji - comprehensive mapping
function getCountryFlag(country) {
  if (!country) return '🌍';

  // Normalize country name for lookup
  const normalized = country.trim();

  const countryFlags = {
    // Africa
    'Nigeria': '🇳🇬', 'NG': '🇳🇬',
    'Ghana': '🇬🇭', 'GH': '🇬🇭',
    'Kenya': '🇰🇪', 'KE': '🇰🇪',
    'South Africa': '🇿🇦', 'ZA': '🇿🇦',
    'Uganda': '🇺🇬', 'UG': '🇺🇬',
    'Tanzania': '🇹🇿', 'TZ': '🇹🇿',
    'Rwanda': '🇷🇼', 'RW': '🇷🇼',
    'Cameroon': '🇨🇲', 'CM': '🇨🇲',
    'Senegal': '🇸🇳', 'SN': '🇸🇳',
    'Ivory Coast': '🇨🇮', 'Côte d\'Ivoire': '🇨🇮', 'CI': '🇨🇮',
    'Zimbabwe': '🇿🇼', 'ZW': '🇿🇼',
    'Zambia': '🇿🇲', 'ZM': '🇿🇲',
    'Botswana': '🇧🇼', 'BW': '🇧🇼',
    'Malawi': '🇲🇼', 'MW': '🇲🇼',
    'Ethiopia': '🇪🇹', 'ET': '🇪🇹',
    'Egypt': '🇪🇬', 'EG': '🇪🇬',
    'Morocco': '🇲🇦', 'MA': '🇲🇦',
    'Tunisia': '🇹🇳', 'TN': '🇹🇳',
    'Algeria': '🇩🇿', 'DZ': '🇩🇿',
    'Libya': '🇱🇾', 'LY': '🇱🇾',
    'Sudan': '🇸🇩', 'SD': '🇸🇩',
    'Angola': '🇦🇴', 'AO': '🇦🇴',
    'Mozambique': '🇲🇿', 'MZ': '🇲🇿',
    'Madagascar': '🇲🇬', 'MG': '🇲🇬',
    'Democratic Republic of the Congo': '🇨🇩', 'DRC': '🇨🇩', 'CD': '🇨🇩',
    'Republic of the Congo': '🇨🇬', 'Congo': '🇨🇬', 'CG': '🇨🇬',
    'Benin': '🇧🇯', 'BJ': '🇧🇯',
    'Togo': '🇹🇬', 'TG': '🇹🇬',
    'Burkina Faso': '🇧🇫', 'BF': '🇧🇫',
    'Mali': '🇲🇱', 'ML': '🇲🇱',
    'Niger': '🇳🇪', 'NE': '🇳🇪',
    'Gabon': '🇬🇦', 'GA': '🇬🇦',
    'Namibia': '🇳🇦', 'NA': '🇳🇦',
    'Mauritius': '🇲🇺', 'MU': '🇲🇺',
    'Liberia': '🇱🇷', 'LR': '🇱🇷',
    'Sierra Leone': '🇸🇱', 'SL': '🇸🇱',
    'Gambia': '🇬🇲', 'GM': '🇬🇲',
    'Guinea': '🇬🇳', 'GN': '🇬🇳',

    // Americas
    'United States': '🇺🇸', 'United States of America': '🇺🇸', 'USA': '🇺🇸', 'US': '🇺🇸', 'America': '🇺🇸',
    'Canada': '🇨🇦', 'CA': '🇨🇦',
    'Mexico': '🇲🇽', 'MX': '🇲🇽',
    'Brazil': '🇧🇷', 'BR': '🇧🇷',
    'Argentina': '🇦🇷', 'AR': '🇦🇷',
    'Colombia': '🇨🇴', 'CO': '🇨🇴',
    'Chile': '🇨🇱', 'CL': '🇨🇱',
    'Peru': '🇵🇪', 'PE': '🇵🇪',
    'Venezuela': '🇻🇪', 'VE': '🇻🇪',
    'Ecuador': '🇪🇨', 'EC': '🇪🇨',
    'Bolivia': '🇧🇴', 'BO': '🇧🇴',
    'Paraguay': '🇵🇾', 'PY': '🇵🇾',
    'Uruguay': '🇺🇾', 'UY': '🇺🇾',
    'Costa Rica': '🇨🇷', 'CR': '🇨🇷',
    'Panama': '🇵🇦', 'PA': '🇵🇦',
    'Guatemala': '🇬🇹', 'GT': '🇬🇹',
    'Honduras': '🇭🇳', 'HN': '🇭🇳',
    'El Salvador': '🇸🇻', 'SV': '🇸🇻',
    'Nicaragua': '🇳🇮', 'NI': '🇳🇮',
    'Jamaica': '🇯🇲', 'JM': '🇯🇲',
    'Trinidad and Tobago': '🇹🇹', 'TT': '🇹🇹',
    'Dominican Republic': '🇩🇴', 'DO': '🇩🇴',
    'Haiti': '🇭🇹', 'HT': '🇭🇹',
    'Cuba': '🇨🇺', 'CU': '🇨🇺',
    'Puerto Rico': '🇵🇷', 'PR': '🇵🇷',

    // Europe
    'United Kingdom': '🇬🇧', 'UK': '🇬🇧', 'GB': '🇬🇧', 'Great Britain': '🇬🇧', 'England': '🏴󠁧󠁢󠁥󠁮󠁧󠁿',
    'Germany': '🇩🇪', 'DE': '🇩🇪',
    'France': '🇫🇷', 'FR': '🇫🇷',
    'Italy': '🇮🇹', 'IT': '🇮🇹',
    'Spain': '🇪🇸', 'ES': '🇪🇸',
    'Portugal': '🇵🇹', 'PT': '🇵🇹',
    'Netherlands': '🇳🇱', 'NL': '🇳🇱', 'Holland': '🇳🇱',
    'Belgium': '🇧🇪', 'BE': '🇧🇪',
    'Switzerland': '🇨🇭', 'CH': '🇨🇭',
    'Austria': '🇦🇹', 'AT': '🇦🇹',
    'Sweden': '🇸🇪', 'SE': '🇸🇪',
    'Norway': '🇳🇴', 'NO': '🇳🇴',
    'Denmark': '🇩🇰', 'DK': '🇩🇰',
    'Finland': '🇫🇮', 'FI': '🇫🇮',
    'Ireland': '🇮🇪', 'IE': '🇮🇪',
    'Poland': '🇵🇱', 'PL': '🇵🇱',
    'Czech Republic': '🇨🇿', 'Czechia': '🇨🇿', 'CZ': '🇨🇿',
    'Hungary': '🇭🇺', 'HU': '🇭🇺',
    'Romania': '🇷🇴', 'RO': '🇷🇴',
    'Greece': '🇬🇷', 'GR': '🇬🇷',
    'Turkey': '🇹🇷', 'TR': '🇹🇷', 'Türkiye': '🇹🇷',
    'Russia': '🇷🇺', 'RU': '🇷🇺',
    'Ukraine': '🇺🇦', 'UA': '🇺🇦',

    // Asia
    'India': '🇮🇳', 'IN': '🇮🇳',
    'China': '🇨🇳', 'CN': '🇨🇳',
    'Japan': '🇯🇵', 'JP': '🇯🇵',
    'South Korea': '🇰🇷', 'Korea': '🇰🇷', 'KR': '🇰🇷',
    'Indonesia': '🇮🇩', 'ID': '🇮🇩',
    'Philippines': '🇵🇭', 'PH': '🇵🇭',
    'Vietnam': '🇻🇳', 'VN': '🇻🇳',
    'Thailand': '🇹🇭', 'TH': '🇹🇭',
    'Malaysia': '🇲🇾', 'MY': '🇲🇾',
    'Singapore': '🇸🇬', 'SG': '🇸🇬',
    'Pakistan': '🇵🇰', 'PK': '🇵🇰',
    'Bangladesh': '🇧🇩', 'BD': '🇧🇩',
    'Sri Lanka': '🇱🇰', 'LK': '🇱🇰',
    'Nepal': '🇳🇵', 'NP': '🇳🇵',
    'Myanmar': '🇲🇲', 'Burma': '🇲🇲', 'MM': '🇲🇲',
    'Cambodia': '🇰🇭', 'KH': '🇰🇭',
    'Taiwan': '🇹🇼', 'TW': '🇹🇼',
    'Hong Kong': '🇭🇰', 'HK': '🇭🇰',

    // Middle East
    'United Arab Emirates': '🇦🇪', 'UAE': '🇦🇪', 'AE': '🇦🇪',
    'Saudi Arabia': '🇸🇦', 'SA': '🇸🇦',
    'Qatar': '🇶🇦', 'QA': '🇶🇦',
    'Kuwait': '🇰🇼', 'KW': '🇰🇼',
    'Bahrain': '🇧🇭', 'BH': '🇧🇭',
    'Oman': '🇴🇲', 'OM': '🇴🇲',
    'Jordan': '🇯🇴', 'JO': '🇯🇴',
    'Lebanon': '🇱🇧', 'LB': '🇱🇧',
    'Israel': '🇮🇱', 'IL': '🇮🇱',
    'Iran': '🇮🇷', 'IR': '🇮🇷',
    'Iraq': '🇮🇶', 'IQ': '🇮🇶',

    // Oceania
    'Australia': '🇦🇺', 'AU': '🇦🇺',
    'New Zealand': '🇳🇿', 'NZ': '🇳🇿',
    'Fiji': '🇫🇯', 'FJ': '🇫🇯',
    'Papua New Guinea': '🇵🇬', 'PG': '🇵🇬',
  };

  // Try direct lookup first
  if (countryFlags[normalized]) {
    return countryFlags[normalized];
  }

  // Try case-insensitive lookup
  const lowerNormalized = normalized.toLowerCase();
  for (const [key, flag] of Object.entries(countryFlags)) {
    if (key.toLowerCase() === lowerNormalized) {
      return flag;
    }
  }

  // Return globe emoji as fallback
  return '🌍';
}
